import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type ConfirmLedgerMatchesResult,
  formatMoney,
  fromCents,
  LEDGER_MATCH_DAYS,
  LEDGER_MATCH_STATE_LABELS,
  LEDGER_MATCH_STATES,
  type LedgerCycle,
  type LedgerMatch,
  type LedgerMatchesResponse,
  type LedgerMatchState,
  toCents,
} from '@k-management/shared';
import { useState } from 'react';
import { Link } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { Icon } from '../../components/Icon.js';
import { amountSort, SortHeader, useTableSort } from '../../components/SortHeader.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { useSessionValue } from '../../lib/preferences.js';
import { CreditNoteMark } from './treasury-ui.js';

const matchesKey = (year: number) => ['treasury', 'ledgers', year, 'matches'] as const;

const STATE_PILLS: Record<LedgerMatchState, string> = {
  pronto: 'pill ok',
  'da-guardare': 'pill warn',
  'senza-movimento': 'pill info',
  'a-posto': 'pill muted',
};

/**
 * Tesoreria › Mastrini › Abbinamenti (12/10/2026): the payments the
 * accountant wrote in the ledgers of the year, each with the invoice it
 * settles and the movement of the bank or of a card found for it. Confirming
 * reconciles the movement (or marks the payment on the day of the ledger) as
 * the reconciliation does; it is undone in the same places.
 */
export function LedgerMatchesView({ year }: { year: number }) {
  const canWrite = useCan('tesoreria.write');
  const queryClient = useQueryClient();
  const [state, setState] = useSessionValue<LedgerMatchState>(
    'list.ledger-matches.state',
    'pronto',
  );
  const [cycle, setCycle] = useSessionValue<LedgerCycle | 'all'>(
    'list.ledger-matches.cycle',
    'all',
  );
  const [query, setQuery] = useSessionValue('list.ledger-matches.query', '');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const data = useQuery({
    queryKey: matchesKey(year),
    queryFn: () => apiRequest<LedgerMatchesResponse>(`/treasury/ledgers/${year}/matches`),
  });
  const confirm = useMutation({
    mutationFn: (keys: string[]) =>
      apiRequest<ConfirmLedgerMatchesResult>(`/treasury/ledgers/${year}/matches/confirm`, {
        method: 'POST',
        body: { keys },
      }),
    onSuccess: async () => {
      setSelected(new Set());
      await queryClient.invalidateQueries({ queryKey: ['treasury'] });
    },
  });

  const all = data.data?.matches ?? [];
  const needle = query.trim().toLowerCase();
  const ofCycle = all.filter((match) => cycle === 'all' || match.cycle === cycle);
  // The tiles count what the search finds, so a subject shows in which group its lines are.
  const searched = ofCycle.filter(
    (match) =>
      !needle ||
      [
        match.accountName,
        match.subject?.name,
        ...match.lines.map((line) => `${line.causal ?? ''} ${line.description ?? ''}`),
        ...match.invoices.map((invoice) => invoice.number),
        match.amount,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(needle),
  );
  const rows = searched.filter((match) => match.state === state);
  const sorting = useTableSort('ledger-matches', rows, {
    date: (match) => match.date,
    subject: (match) => match.subject?.name ?? match.accountName,
    line: (match) => match.lines[0]?.causal ?? match.lines[0]?.description ?? null,
    amount: (match) => amountSort(match.amount),
    invoice: (match) => match.invoices[0]?.number ?? null,
  });
  const selectable = rows.filter((match) => canWrite && match.confirmable);
  const chosen = all.filter((match) => selected.has(match.key));
  const sum = (list: LedgerMatch[]) =>
    fromCents(list.reduce((total, match) => total + toCents(match.amount), 0));
  const toggle = (key: string) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelected(next);
  };

  return (
    <div className="stack">
      <p className="muted small">
        Le righe di pagamento e incasso del mastrino {year} («Pagamento Ft …», «Incasso Ft …»,
        abbuoni), con la fattura che citano e il movimento di banca
        {data.data?.cards ? ' o di carta' : ''} con lo stesso importo entro {LEDGER_MATCH_DAYS}{' '}
        giorni, non ancora riconciliato. Il mastrino propone soltanto: si conferma qui, e si annulla
        dal movimento, dalla spesa con carta o dalla fattura come sempre. Quello che hai già
        riconciliato non viene toccato.
      </p>
      <div className="treasury-tiles">
        {LEDGER_MATCH_STATES.map((key) => {
          const list = searched.filter((match) => match.state === key);
          return (
            <button
              key={key}
              type="button"
              className={`treasury-tile${state === key ? ' selected' : ''}`}
              onClick={() => {
                setState(key);
                setSelected(new Set());
              }}
            >
              <span>{LEDGER_MATCH_STATE_LABELS[key]}</span>
              <strong>{list.length}</strong>
              <small className="muted">{formatMoney(sum(list))}</small>
            </button>
          );
        })}
      </div>

      <div className="ledger-toolbar">
        <label className="search-box">
          <Icon name="search" />
          <input
            value={query}
            placeholder="Soggetto, numero di fattura o importo"
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        <select
          className="compact-select"
          aria-label="Ciclo"
          value={cycle}
          onChange={(event) => setCycle(event.target.value as LedgerCycle | 'all')}
        >
          <option value="all">Clienti e fornitori</option>
          <option value="attivo">Solo clienti (incassi)</option>
          <option value="passivo">Solo fornitori (pagamenti)</option>
        </select>
        {selectable.length > 0 && (
          <button
            type="button"
            onClick={() =>
              setSelected(
                selected.size === selectable.length
                  ? new Set()
                  : new Set(selectable.map((match) => match.key)),
              )
            }
          >
            {selected.size === selectable.length ? 'Togli la selezione' : 'Seleziona tutti'}
          </button>
        )}
        {canWrite && (
          <button
            type="button"
            className="primary"
            disabled={chosen.length === 0 || confirm.isPending}
            onClick={() => confirm.mutate(chosen.map((match) => match.key))}
          >
            {confirm.isPending
              ? 'Attendere…'
              : `Conferma ${chosen.length === 1 ? '1 selezionato' : `${chosen.length} selezionati`}${chosen.length ? ` (${formatMoney(sum(chosen))})` : ''}`}
          </button>
        )}
      </div>
      {state === 'senza-movimento' && (
        <p className="notice small">
          Senza movimento: confermare segna l’incasso o il pagamento con la data del mastrino, come
          a mano. Se poi carichi il movimento della banca, riconciliandolo con la stessa fattura e
          lo stesso importo si collega a questo.
        </p>
      )}
      {confirm.data && (
        <div className={confirm.data.failed.length ? 'notice' : 'notice info'}>
          {confirm.data.done === 1
            ? '1 abbinamento confermato.'
            : `${confirm.data.done} abbinamenti confermati.`}
          {confirm.data.failed.length > 0 && (
            <>
              {' '}
              Non riusciti: {confirm.data.failed.length}
              <ul className="small">
                {confirm.data.failed.slice(0, 10).map((item) => (
                  <li key={item.key}>
                    {describe(all.find((match) => match.key === item.key))}: {item.error}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
      {confirm.error && <p className="error">{confirm.error.message}</p>}
      {data.isError && <p className="error">{data.error.message}</p>}

      <div className="table-wrap">
        <table className="data-table ledger-table">
          <thead>
            <tr>
              <th aria-label="Scelta" />
              <SortHeader sort={sorting} column="date">
                Data
              </SortHeader>
              <SortHeader sort={sorting} column="subject">
                Soggetto
              </SortHeader>
              <SortHeader sort={sorting} column="line">
                Riga del mastrino
              </SortHeader>
              <SortHeader sort={sorting} column="amount" numeric>
                Importo
              </SortHeader>
              <SortHeader sort={sorting} column="invoice">
                Fattura in Tesoreria
              </SortHeader>
              <th>Movimento</th>
            </tr>
          </thead>
          <tbody>
            {sorting.rows.map((match) => (
              <tr key={match.key}>
                <td>
                  {canWrite && match.confirmable && (
                    <input
                      type="checkbox"
                      aria-label="Scegli"
                      checked={selected.has(match.key)}
                      onChange={() => toggle(match.key)}
                    />
                  )}
                </td>
                <td>{formatDate(match.date)}</td>
                <td>
                  {match.subject ? (
                    <Link to={`/anagrafica/${match.subject.id}`}>{match.subject.name}</Link>
                  ) : (
                    match.accountName
                  )}
                  <small className="muted block">
                    {match.cycle === 'attivo' ? 'cliente' : 'fornitore'}
                  </small>
                </td>
                <td>
                  {match.lines.map((line) => (
                    <span key={line.entryId} className="block">
                      {line.causal ?? line.description}
                      {match.lines.length > 1 && (
                        <small className="muted"> · {formatMoney(line.amount)}</small>
                      )}
                    </span>
                  ))}
                  {match.problem && (
                    <small
                      className={`block ${match.state === 'da-guardare' ? 'warn-text' : 'muted'}`}
                    >
                      {match.problem}
                    </small>
                  )}
                </td>
                <td className="numeric">
                  <strong>{formatMoney(match.amount)}</strong>
                </td>
                <td>
                  {match.invoices.length === 0 && <span className="muted">—</span>}
                  {match.invoices.map((invoice) => (
                    <span key={invoice.invoiceId} className="block">
                      <CreditNoteMark kind={invoice.kind} />
                      <Link to={`/tesoreria/fatture/${invoice.invoiceId}`}>
                        {invoice.number}
                      </Link>{' '}
                      <small className="muted">
                        del {formatDate(invoice.date)}
                        {match.invoices.length > 1 && ` · ${formatMoney(invoice.amount)}`}
                      </small>
                    </span>
                  ))}
                </td>
                <td>
                  <MovementCell match={match} />
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="muted">
                  {data.isPending ? 'Caricamento…' : 'Nessuna riga con questi filtri.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MovementCell({ match }: { match: LedgerMatch }) {
  const movement = match.movement;
  if (!movement) {
    return (
      <span className={STATE_PILLS[match.state]}>
        {match.state === 'a-posto' ? 'già a posto' : 'nessun movimento'}
      </span>
    );
  }
  return (
    <>
      {movement.source === 'banca' ? (
        <a
          href={`/tesoreria/banche/${encodeURIComponent(movement.accountCode!)}/movimenti/${movement.id}/riconcilia`}
          target="_blank"
          rel="noreferrer"
        >
          {movement.label} {formatDate(movement.date)}
        </a>
      ) : (
        <span>
          {movement.label} {formatDate(movement.date)}
        </span>
      )}{' '}
      <strong>{formatMoney(movement.amount)}</strong>
      {match.alternatives > 0 && (
        <span className="pill warn">
          {match.alternatives === 1 ? '1 altro possibile' : `${match.alternatives} altri possibili`}
        </span>
      )}
      <small className="muted block">{movement.description}</small>
    </>
  );
}

function describe(match: LedgerMatch | undefined): string {
  if (!match) return 'Proposta';
  return `${formatDate(match.date)} ${match.subject?.name ?? match.accountName} ${formatMoney(match.amount)}`;
}
